// src/components/exercise/exercises/TrueFalseExercise.tsx
import React, { useEffect, useState } from 'react';
import type { Exercise, ExerciseUserInput, GradeResult } from '../../../types/exercise';
import { useThemePalette } from '../../../hooks/useThemePalette';
import { useThemeStore }   from '../../../stores/useThemeStore';

interface Props {
  exercise: Exercise;
  onChange: (input: ExerciseUserInput) => void;
  isSubmitted: boolean;
  grade: GradeResult | null;
}

const TrueFalseExercise: React.FC<Props> = ({ exercise, onChange, isSubmitted, grade }) => {
  const C      = useThemePalette();
  const isDark = useThemeStore(s => s.theme) === 'dark';
  const [selected,   setSelected]   = useState<boolean | null>(null);
  const [trueHover,  setTrueHover]  = useState(false);
  const [falseHover, setFalseHover] = useState(false);

  useEffect(() => { setSelected(null); }, [exercise]);

  const pick = (value: boolean) => {
    if (isSubmitted) return;
    setSelected(value);
    const input: ExerciseUserInput = exercise.type === 'true_false_correction'
      ? { type: 'true_false_correction', value }
      : { type: 'true_false', value };
    onChange(input);
  };

  const btnStyle = (value: boolean): React.CSSProperties => {
    const isSelected = selected === value;
    const isHovered  = value ? trueHover : falseHover;

    let bg     = isDark ? 'rgba(255,255,255,0.03)' : C.card;
    let border = C.border;
    let color  = C.muted;

    if (isSelected && !isSubmitted) {
      bg = 'rgba(79,140,247,0.12)'; border = C.accent; color = C.text;
    }
    if (isSubmitted && isSelected) {
      if (grade?.isCorrect) {
        bg = 'rgba(52,211,153,0.12)'; border = C.success; color = C.success;
      } else {
        bg = 'rgba(248,113,113,0.1)'; border = C.error; color = C.error;
      }
    }
    if (isSubmitted && !grade?.isCorrect && exercise.correct_answer === value) {
      bg = 'rgba(52,211,153,0.1)'; border = C.success; color = C.success;
    }
    if (!isSubmitted && isHovered && selected !== value) {
      bg = isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)';
      border = C.borderH;
      color  = C.text;
    }

    return {
      flex: 1, padding: '16px 24px', borderRadius: '12px',
      border: `1.5px solid ${border}`, background: bg, color,
      fontSize: '0.9rem', fontWeight: 700, letterSpacing: '0.06em',
      textTransform: 'uppercase', cursor: isSubmitted ? 'default' : 'pointer',
      transition: 'all 0.2s ease', textAlign: 'center', fontFamily: 'inherit',
    };
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      <QuestionBox text={exercise.question} />

      <div style={{ display: 'flex', gap: '12px' }}>
        <button style={btnStyle(true)}  onClick={() => pick(true)}  onMouseEnter={() => setTrueHover(true)}  onMouseLeave={() => setTrueHover(false)}  disabled={isSubmitted} aria-pressed={selected === true}>
          ✓ True
        </button>
        <button style={btnStyle(false)} onClick={() => pick(false)} onMouseEnter={() => setFalseHover(true)} onMouseLeave={() => setFalseHover(false)} disabled={isSubmitted} aria-pressed={selected === false}>
          ✗ False
        </button>
      </div>

      {isSubmitted && grade && (
        <FeedbackBox isCorrect={grade.isCorrect} correctDisplay={!grade.isCorrect ? grade.correctAnswerDisplay : undefined} explanation={exercise.explanation} />
      )}
    </div>
  );
};

export default TrueFalseExercise;

// ── Shared sub-components used across exercise types ─────────────────────────

export function QuestionBox({ text }: { text: string }) {
  const C      = useThemePalette();
  const isDark = useThemeStore(s => s.theme) === 'dark';
  return (
    <div style={{
      background:     isDark ? 'rgba(255,255,255,0.03)' : C.card,
      border:         `1px solid ${C.border}`,
      borderRadius:   '14px',
      padding:        '20px 24px',
      fontSize:       '1rem',
      lineHeight:     1.7,
      color:          C.text,
      backdropFilter: 'blur(8px)',
    }}>
      {text}
    </div>
  );
}

export function FeedbackBox({
  isCorrect, correctDisplay, explanation,
}: { isCorrect: boolean; correctDisplay?: string; explanation?: string }) {
  const C = useThemePalette();
  return (
    <div style={{
      padding:       '14px 18px',
      borderRadius:  '12px',
      background:    isCorrect ? 'rgba(52,211,153,0.08)' : 'rgba(248,113,113,0.08)',
      border:        `1px solid ${isCorrect ? 'rgba(52,211,153,0.25)' : 'rgba(248,113,113,0.25)'}`,
      display:       'flex',
      flexDirection: 'column',
      gap:           '6px',
    }}>
      <div style={{ fontWeight: 700, color: isCorrect ? C.success : C.error, fontSize: '0.85rem', letterSpacing: '0.04em' }}>
        {isCorrect ? '✓ Correct' : '✗ Incorrect'}
      </div>
      {!isCorrect && correctDisplay && (
        <div style={{ color: C.muted, fontSize: '0.85rem' }}>
          Correct answer: <span style={{ color: C.success, fontWeight: 600 }}>{correctDisplay}</span>
        </div>
      )}
      {explanation && (
        <div style={{ color: C.muted, fontSize: '0.82rem', marginTop: '4px', lineHeight: 1.6 }}>
          {explanation}
        </div>
      )}
    </div>
  );
}
